(ns app.front.view.admin.domain-groups.batch-group-creation
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.combo-box :as ui-combo]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.util :as futil]
            [app.front.view.admin.domain-groups.subs :as domain-groups-subs]
            [app.front.view.admin.superdomain :as admin-superdomain]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.domain.validators :as v]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn- find-entity [db id]
  (-> db entities/db->context (entities/find-entity id)))

(def state-keys
  [::show-validation-result
   ::failures
   ::municipality-validation-results
   ::completion-notified-batches
   ::running-batch
   ::start-batch-group-creation-in-progress?
   ::group-batch-items
   ::source-domain
   ::target-superdomain-group
   ::expanded-rows
   ::running-batch-items
   ::finished-group
   ::finished-group-with-failure])

(rf/reg-event-fx ::job-fetched
  (fn [{:keys [db]} [_ _status response]]
    (if (empty? response)
      {:db (apply dissoc db state-keys)}
      (let [{:keys [batch-id
                    items
                    source-domain
                    state
                    target-superdomain-group]} response
            running-batch-items                (->> items
                                                    (map (fn [[idx {:keys [permit-types auth-groups-finished auth-groups-failed]}]]
                                                           [idx (-> permit-types
                                                                    (set/difference auth-groups-finished)
                                                                    (set/difference auth-groups-failed))]))
                                                    (into {}))
            finished-groups                    (->> items
                                                    (map (fn [[idx {:keys [auth-groups-finished]}]]
                                                           [idx auth-groups-finished]))
                                                    (into {}))
            failed-groups                      (->> items
                                                    (map (fn [[idx {:keys [auth-groups-failed]}]]
                                                           [idx auth-groups-failed]))
                                                    (into {}))
            batch-finished?                    (->> running-batch-items vals (every? empty?))
            has-failures?                      (->> failed-groups vals (remove empty?) seq boolean)
            completion-notified?               (contains? (::completion-notified-batches db #{}) batch-id)
            db'                                (-> db
                                                   (merge {::running-batch            batch-id
                                                           ::group-batch-items        items
                                                           ::source-domain            (find-entity db source-domain)
                                                           ::target-superdomain-group (find-entity db target-superdomain-group)
                                                           ::expanded-rows            (set (keys items))})
                                                   (assoc-in [::running-batch-items batch-id] running-batch-items)
                                                   (assoc-in [::state batch-id] state)
                                                   (assoc-in [::finished-group batch-id] finished-groups)
                                                   (assoc-in [::finished-group-with-failure batch-id] failed-groups))]
        (if (and batch-finished? (not completion-notified?))
          {:db       (update db' ::completion-notified-batches (fnil conj #{}) batch-id)
           :dispatch (if has-failures?
                       [:notifications/add :toast :warning :warning/warning :admin/batch-group-creation-complete-with-errors]
                       [:notifications/add :toast :success :success :admin/batch-group-creation-complete])}
          {:db db'})))))

(rf/reg-event-fx ::fetch-running-job
  (fn [_ _]
    {:comm/command {:command    :admin/get-latest-running-batch-group-creation-job
                    :method     :get
                    :on-done    [::loading-done]
                    :on-success [::job-fetched]}}))

(rf/reg-event-fx ::fetch-job-by-id
  (fn [_ [_ batch-id]]
    {:comm/command {:command    :admin/get-batch-group-creation-job
                    :method     :get
                    :data       {:batch-id batch-id}
                    :on-success [::job-fetched]}}))

(rf/reg-event-fx ::fetch-job-by-id-debounce
  (fn [_ [_ batch-id]]
    {:dispatch-debounce [{:id      (str "fetch-job-by-id-debounce-" batch-id)
                          :timeout 300
                          :action  :dispatch
                          :event   [::fetch-job-by-id batch-id]}]}))

(rf/reg-event-fx ::validate-municipality
  (fn [{db :db} [_ idx short-name]]
    (if (str/blank? short-name)
      {:db (assoc-in db [::municipality-validation-results idx] {})}
      (when-let [source-domain-id (-> db ::source-domain :db/id)]
        {:comm/command {:command    :admin/validate-municipality
                        :method     :post
                        :data       {:source-domain source-domain-id
                                     :short-name    short-name}
                        :on-success [::validate-municipality.success idx]}
         :db           (assoc-in db [::municipality-validation-results idx] {:state :loading})}))))

(rf/reg-event-db ::validate-municipality.success
  (fn [db [_ idx _ resp]]
    (assoc-in db [::municipality-validation-results idx] resp)))

(rf/reg-sub ::municipality-validation
  (fn [db [_ idx]]
    (get-in db [::municipality-validation-results idx])))

(rf/reg-event-fx ::fetch-countries-and-merge-groups
  (fn [{:keys [db]} [_ status response]]
    {:db         (assoc db
                        ::categories (:categories response)
                        ::countries (:countries response)
                        ::timezones (:timezones response)
                        ::currencies (:currencies response))
     :dispatch-n [[::entities/override-entity-db status (:entities response)]
                  [::domain-groups-subs/fetch-municipalities-public]
                  [::fetch-running-job]]}))

(rf/reg-event-db ::loading-done
  (fn [db _]
    (dissoc db ::groups-loading?)))

(rf/reg-event-fx ::fetch-municipalities
  (fn [{:keys [db]} _]
    {:comm/command {:command    :admin/municipality-groups
                    :method     :get
                    :on-success [::fetch-countries-and-merge-groups]}
     :db           (assoc db ::groups-loading? true)}))

(rf/reg-sub ::lower-tier-authority-groups
  :<- [::entities/context]
  (fn [ctx _]
    (entities/find-entities ctx {:group/type :group.type/application-authority})))

(rf/reg-sub ::superdomain-groups
  :<- [::entities/context]
  (fn [ctx _]
    (entities/find-entities ctx {:group/type :group.type/superdomain})))

(rf/reg-sub ::lower-tier-domain-groups
  :<- [::lower-tier-authority-groups]
  (fn [authority-groups _]
    (->> authority-groups
         (map :entity/parent)
         (filter :group/name)
         distinct
         (sort-by :group/name))))

(rf/reg-event-fx ::refresh-job
  (fn [_ [_ batch-id]]
    {:dispatch [::fetch-job-by-id-debounce batch-id]}))

(rf/reg-event-fx ::creating-group-failed
  (fn [{db :db} [_ batch-id idx group-id]]
    {:db       (assoc-in db [::failures batch-id idx] [[:error/group-id-already-used] [group-id]])
     :dispatch [::fetch-job-by-id-debounce batch-id]}))

(rf/reg-event-fx ::municipality-not-found
  (fn [{db :db} [_ batch-id idx short-name]]
    {:db       (assoc-in db [::failures batch-id idx] [[:error/municipality-not-found] [short-name]])
     :dispatch [::fetch-job-by-id-debounce batch-id]}))

(rf/reg-event-fx ::all-jobs-failed
  (fn [_ [_ batch-id]]
    {:dispatch-n [[::fetch-job-by-id-debounce batch-id]
                  [:notifications/add :toast :error :error/error :error/all-batch-jobs-failed]]}))

(rf/reg-event-fx ::save-config-error
  (fn [_ [_ batch-id error-message]]
    {:dispatch-n [[::fetch-job-by-id-debounce batch-id]
                  [:notifications/add :toast :error :error/error error-message]]}))

(rf/reg-event-fx ::creating-group-error
  (fn [_ [_ batch-id error-message]]
    {:dispatch-n [[::fetch-job-by-id-debounce batch-id]
                  [:notifications/add :toast :error :error/error error-message]]}))

(rf/reg-sub ::authority-group-failed?
  (fn [db [_ batch-id idx permit-type]]
    (boolean (get-in db [::finished-group-with-failure batch-id idx permit-type]))))

(rf/reg-sub ::failures
  (fn [db [_ batch-id idx]]
    (get-in db [::failures batch-id idx])))

(rf/reg-sub ::group-creation-done?
  (fn [db [_ batch-id idx permit-type]]
    (boolean (get-in db [::finished-group batch-id idx permit-type]))))

(rf/reg-sub ::running-batch-items
  :-> ::running-batch-items)

(rf/reg-sub ::finished-group
  :-> ::finished-group)

(rf/reg-sub ::finished-group-with-failure
  :-> ::finished-group-with-failure)

(rf/reg-sub ::state
  :-> ::state)

(rf/reg-sub ::batch-progress
  :<- [::running-batch]
  :<- [::state]
  :<- [::finished-group]
  :<- [::finished-group-with-failure]
  :<- [::group-batch-items]
  (fn [[batch-id state finished-by-batch failed-by-batch items] _]
    (let [finished     (get finished-by-batch batch-id)
          failed       (get failed-by-batch batch-id)
          success-fail (group-by :domain/state items)]
      {:batch-id                  batch-id
       :state                     (get state batch-id)
       :total-domain-groups       (count items)
       :successful-domain-groups  (->> success-fail :async-job.state/ready count)
       :failed-domain-groups      (->> success-fail :async-job.state/failed count)
       :total-authority-groups    (->> items (map #(count (:permit-types %))) (reduce + 0))
       :finished-authority-groups (->> (vals finished) (map count) (reduce + 0))
       :failed-authority-groups   (->> (vals failed) (map count) (reduce + 0))})))

(rf/reg-sub ::item-running?
  (fn [db [_ batch-id idx permit-type]]
    (boolean (get-in db [::running-batch-items batch-id idx permit-type]))))

(rf/reg-event-fx ::start-batch-group-creation.success
  (fn [{db :db} [_ _status response]]
    (let [batch-id (:batch-id response)]
      {:db       (-> db
                     (assoc ::running-batch batch-id)
                     (assoc-in [::state batch-id] :async-job.state/running))
       :dispatch [::fetch-job-by-id batch-id]})))

(rf/reg-sub ::running-batch
  :-> ::running-batch)

(rf/reg-sub ::start-batch-group-creation-in-progress?
  :-> ::start-batch-group-creation-in-progress?)

(rf/reg-event-fx ::start-batch-group-creation
  (fn [{:keys [db]} _]
    (let [source-domain            (-> db ::source-domain)
          target-superdomain-group (-> db ::target-superdomain-group)
          origin-pt-groups         (group-model/get-permit-types source-domain)
          items                    (->> (::group-batch-items db)
                                        vals
                                        (map (fn [municipality-group]
                                               (reduce (fn [mg permit-type]
                                                         (cond-> mg
                                                           (and (contains? origin-pt-groups permit-type)
                                                                (get mg permit-type))
                                                           (update :permit-types (fnil conj #{}) permit-type)

                                                           :always
                                                           (dissoc permit-type)))
                                                       municipality-group
                                                       ptc-model/primary-permit-types)))
                                        (map (fn [mg]
                                               (select-keys mg [:group-name :group-domain :short-name :official-name :permit-types :idx]))))]
      {:comm/command {:command          :admin/start-batch-group-creation
                      :method           :post
                      :data             (p/assoc-if {:source-domain (:db/id source-domain)
                                                     :items         items}
                                                    :target-superdomain-group (:db/id target-superdomain-group))
                      :notify           {:success :admin/batch-group-creation-in-progress}
                      :finally-dispatch [::clear-start-batch-group-creation-in-progress]
                      :on-success       [::start-batch-group-creation.success]}
       :db           (assoc db ::start-batch-group-creation-in-progress? true)})))

(rf/reg-event-db ::clear-start-batch-group-creation-in-progress
  (fn [db _]
    (dissoc db ::start-batch-group-creation-in-progress?)))

(rf/reg-event-fx ::stop-batch-group-creation
  (fn [{db :db} [_ batch-id]]
    (if (-> (-> db ::state (get batch-id)) (= :async-job.state/running))
      {:comm/command {:command    :admin/cancel-batch-group-creation-job
                      :method     :post
                      :data       {:batch-id (::running-batch db)}
                      :notify     [:warning :admin/batch-group-creation-cancelled]
                      :on-success [::fetch-job-by-id-debounce (::running-batch db)]}}
      {:db (apply dissoc db state-keys)})))

(rf/reg-event-db ::open-modal
  (fn [db _]
    (assoc db ::open-modal? true)))

(rf/reg-event-db ::close-modal
  (fn [db _]
    (assoc db ::open-modal? false)))

(rf/reg-sub ::modal-open?
  :-> ::open-modal?)

(defmulti validate (fn [field _] field))

(def required-fields #{:group-name :group-domain :short-name})

(rf/reg-sub ::valid-request?
  :<- [::source-domain]
  :<- [::group-batch-items]
  :<- [::validation-context]
  (fn [[source-domain items validation-ctx] _]
    (and source-domain
         (seq items)
         (->> items
              (mapcat #(map (fn [field] (validate field % validation-ctx)) required-fields))
              (every? nil?))
         (->> items
              (map #(some % ptc-model/primary-permit-types))
              (every? true?)))))

(rf/reg-sub ::group-batch-items
  (fn [db _]
    (->> db
         ::group-batch-items
         vals
         (sort-by :idx))))

(rf/reg-event-db ::add-new-item
  (fn [db [_ item]]
    (update db ::group-batch-items assoc (:idx item) item)))

(rf/reg-event-db ::remove-item
  (fn [db [_ idx]]
    (update db ::group-batch-items dissoc idx)))

(rf/reg-sub ::source-domain
  :-> ::source-domain)

(rf/reg-sub ::source-domain-descendants
  :<- [::source-domain]
  :-> group-model/descendant-authority-groups)

(rf/reg-sub ::available-permit-types
  :<- [::source-domain]
  (fn [groups [_ tr]]
    (cond->> (group-model/get-permit-types groups)
      :always
      (set/intersection ptc-model/primary-permit-types)

      tr
      (sort-by (fn [x] (tr [x]))))))

(rf/reg-sub ::municipalities
  :<- [::source-domain-descendants]
  (fn [groups _]
    (distinct (mapcat group-model/scope-municipalities groups))))

(rf/reg-sub ::country
  :<- [::municipalities]
  (fn [municipalities _]
    (some :municipality/country municipalities)))

(rf/reg-sub ::subdivision
  :<- [::municipalities]
  (fn [municipalities _]
    (some :municipality/subdivision municipalities)))

(rf/reg-event-db ::set-source-domain
  (fn [db [_ domain]]
    (assoc db ::source-domain domain)))

(rf/reg-event-db ::update-item-field
  (fn [db [_ idx field value]]
    (assoc-in db [::group-batch-items idx field] value)))

(rf/reg-event-db ::show-validation-result
  (fn [db [_ idx field]]
    (assoc-in db [::show-validation-result idx field] true)))

(rf/reg-sub ::show-validation
  (fn [db [_ idx field]]
    (get-in db [::show-validation-result idx field])))

(rf/reg-event-db ::set-target-superdomain-group
  (fn [db [_ group]]
    (assoc db ::target-superdomain-group group)))

(rf/reg-event-db ::update-expanded-rows
  (fn [db [_ idx open?]]
    (update db ::expanded-rows (if open? (fnil conj #{}) disj) idx)))

(rf/reg-sub ::expanded-rows
  :-> ::expanded-rows)

(rf/reg-sub ::target-superdomain-group
  :-> ::target-superdomain-group)

(rf/reg-sub ::municipality-validations
  :-> ::municipality-validation-results)

(defn- trimmed [s]
  (some-> s str/trim))

(defn- group-name-used?
  [{:keys [domain-group-names batch-items]} {:keys [idx group-name]}]
  (let [candidate-name (trimmed group-name)
        all-names      (->> (remove #(= idx (:idx %)) batch-items)
                            (keep (comp trimmed :group-name))
                            (into domain-group-names))]
    (and candidate-name
         (contains? all-names candidate-name))))

(defn- short-name-used?
  [{:keys [batch-items]} {:keys [idx short-name]}]
  (let [candidate-name (trimmed short-name)]
    (and candidate-name
         (->> (remove #(= idx (:idx %)) batch-items)
              (keep (comp trimmed :short-name))
              set
              (contains? candidate-name)))))

(rf/reg-sub ::validation-context
  :<- [::lower-tier-domain-groups]
  :<- [::group-batch-items]
  :<- [::municipality-validations]
  (fn [[domain-groups batch-items municipality-validations] _]
    {:domain-group-names       (->> domain-groups
                                    (keep (comp trimmed :group/name))
                                    set)
     :batch-items              batch-items
     :municipality-validations municipality-validations}))

(defmethod validate :default [field row _]
  (when (str/blank? (get row field))
    :required-field))

(defmethod validate :group-domain [field row _]
  (let [group-domain (get row field)]
    (if (str/blank? group-domain)
      :required-field
      (when-not (v/valid-domain? group-domain)
        :error/invalid-url))))

(defmethod validate :group-name [field row validation-ctx]
  (cond
    (str/blank? (get row field))
    :required-field

    (group-name-used? validation-ctx row)
    :error/group-name-must-be-unique))

(defmethod validate :short-name [field row {:keys [municipality-validations] :as validation-ctx}]
  (cond
    (str/blank? (get row field))
    :required-field

    (short-name-used? validation-ctx row)
    :error/municipality-short-name-must-be-unique

    :else
    (let [municipality-data (get municipality-validations (:idx row))]
      (case (:state municipality-data)
        :not-found :error/municipality-not-found
        :occupied [[:error/group-id-already-used] [(:group-id municipality-data)]]
        nil))))

(defmethod validate :official-name [_ _ _])

(defmulti failure (fn [field _row] field))

(defmethod failure :short-name [_field row]
  (<sub [::failures
         (<sub [::running-batch])
         (:idx row)]))

(defmethod failure :default [_ _])

(defn render-checkbox [tr row permit-type disabled?]
  (let [key-id           (str (:idx row) "-" (name permit-type))
        running-batch-id (<sub [::running-batch])]
    (cond
      (<sub [::item-running? running-batch-id (:idx row) permit-type])
      ^{:key key-id}
      [:div.flex.flex-row.flex-1.gap-2.h-10.my-2
       (cc/loading-spinner {:with-overlay false
                            :small        true})
       [:div (tr [permit-type])]]

      (<sub [::authority-group-failed? running-batch-id (:idx row) permit-type])
      ^{:key key-id}
      [:div.flex.flex-row.flex-1.gap-2.h-10.my-2
       (cc/icon {:icon i/warning :description (tr [permit-type])})
       (tr [permit-type])]

      (<sub [::group-creation-done? running-batch-id (:idx row) permit-type])
      ^{:key key-id}
      [:div.flex.flex-row.flex-1.gap-2.h-10.my-2
       (cc/icon {:icon i/check :description (tr [permit-type])})
       (tr [permit-type])]

      :else
      [cc/checkbox
       {:key        key-id
        :id         key-id
        :class-name "h-10"
        :disabled   disabled?
        :checked    (true? (get row permit-type))
        :on-change  #(>evt [::update-item-field (:idx row) permit-type %])
        :label-text (tr [permit-type])}])))

(def labels
  {:official-name :admin/official-name-short
   :short-name    :admin/short-name-of-municipality
   :group-domain  :admin/domain-domain
   :group-name    :admin/group-name})

(defn render-input [tr validation-ctx field row disabled?]
  (let [row-index               (:idx row)
        show-validation-result? (<sub [::show-validation row-index field])
        invalid-text            (or (when show-validation-result? (validate field row validation-ctx))
                                    (failure field row))]
    [ui/text-input
     {:id            (str row-index "-" (name field))
      :label-text    (tr [(get labels field)])
      :required      (boolean (required-fields field))
      :placeholder   (when (#{:official-name} field)
                       (:short-name row))
      :disabled      disabled?
      :invalid       (boolean invalid-text)
      :invalid-text  (if (vector? invalid-text)
                       (apply tr invalid-text)
                       (some->> invalid-text vector tr))
      :default-value (get row field)
      :on-blur       #(do
                        (when-not show-validation-result?
                          (>evt [::show-validation-result row-index field]))
                        (when (#{:short-name} field)
                          (>evt [::validate-municipality row-index (-> % futil/tval str/trim)])))
      :on-change     #(>evt [::update-item-field row-index field (futil/tval %)])}]))

(defn select-all [tr row disabled?]
  (let [available-permit-types (<sub [::available-permit-types])]
    [:div.w-36
     [ui/button
      {:on-click #(doseq [p available-permit-types]
                    (>evt [::update-item-field (:idx row) p true]))
       :disabled disabled?}
      (tr [:select/all])]]))

(defn de-select-all [tr row disabled?]
  [:div.w-36
   [ui/button
    {:on-click #(doseq [p ptc-model/primary-permit-types]
                  (>evt [::update-item-field (:idx row) p false]))
     :disabled disabled?}
    (tr [:select/deselect-all])]])

(defn all-selected? [row]
  (let [available-permit-types (<sub [::available-permit-types])]
    (every? #(true? (get row %)) available-permit-types)))

(defn render-municipality [row]
  (let [municipality-data (<sub [::municipality-validation (:idx row)])]
    (case (:state municipality-data)
      (:available :occupied)
      [:p (:address municipality-data)]

      :loading
      (cc/loading-spinner {:with-overlay false
                           :small        true})

      nil)))

(defn batch-progress-section [tr]
  (let [{:keys [batch-id
                state
                total-domain-groups
                successful-domain-groups
                failed-domain-groups
                total-authority-groups
                finished-authority-groups
                failed-authority-groups]} (<sub [::batch-progress])]
    (when batch-id
      [:section.bx--col
       [:div.mt-4.p-4.bg-gray-50.border.border-gray-200.rounded
        [:div.flex.flex-row.items-center.gap-3.mb-4
         [:h3.font-semibold.text-lg (tr [:admin/batch-progress] [(str/lower-case (tr [state]))])]]
        [:div.flex.flex-row.gap-12
         [:div
          [:p.text-xl.font-semibold
           (tr [:admin/batch-domain-groups-progress] [successful-domain-groups total-domain-groups])]
          (when (some-> failed-domain-groups pos?)
            [:p.text-xl.font-semibold.text-red-600
             (tr [:admin/batch-domain-groups-failed] [failed-domain-groups])])]
         [:div
          [:p.text-xl.font-semibold
           (tr [:admin/batch-authority-groups-progress] [finished-authority-groups total-authority-groups])]
          (when (some-> failed-authority-groups pos?)
            [:p.text-xl.font-semibold.text-red-600
             (tr [:admin/batch-authority-groups-failed] [failed-authority-groups])])]]]])))

(defn view [_]
  (let [tr                       (<sub [:tr])
        domains                  (<sub [::lower-tier-domain-groups])
        superdomain-groups       (<sub [::superdomain-groups])
        domain-group             (<sub [::source-domain])
        target-superdomain-group (<sub [::target-superdomain-group])
        items                    (<sub [::group-batch-items])
        validation-ctx           (<sub [::validation-context])
        expanded-rows            (<sub [::expanded-rows])
        batch-id                 (<sub [::running-batch])
        state                    (<sub [::state])
        start-in-progress?       (boolean (<sub [::start-batch-group-creation-in-progress?]))
        batch-job-running?       (boolean (-> state (get batch-id) (= :async-job.state/running)))
        disabled?                (or start-in-progress? batch-job-running?)
        country                  (<sub [::country])
        subdivision              (<sub [::subdivision])
        save-disabled?           (not (<sub [::valid-request?]))
        available-permit-types   (<sub [::available-permit-types])]
    [:div.cloudpermit-page.admin-page
     [ui/confirm-modal {:open?        (boolean (<sub [::modal-open?]))
                        :id           "cancel-job-modal"
                        :tr           tr
                        :on-close     #(>evt [::close-modal])
                        :on-yes-click #(>evt [::stop-batch-group-creation batch-id])}
      (tr [:async-job-ui/cancel-job-confirm])]
     [:div.bx--row.page-heading.justify-between
      [:h1 (tr [:view/admin.batch-group-creation])]]
     [:section.bx--col
      [:div.px-2.w-1-2
       [:p (tr [:admin/select-source-group])]
       [ui-combo/combo-box {:id          "select-source-group"
                            :items       domains
                            :value       (or domain-group "")
                            :disabled    disabled?
                            :item->str   :group/name
                            :on-change   #(do
                                            (>evt [::set-source-domain %])
                                            (doseq [{:keys [idx short-name]} items]
                                              (>evt [::validate-municipality idx short-name])))
                            :placeholder (tr [:select/select])}]]
      (when domain-group
        [:div.mt-4
         [sortable-table/table
          {:items   [domain-group]
           :id-func :db/id
           :cols    [[(tr [:admin/group-name]) :group/name]
                     [(tr [:admin/country-code]) (constantly country)]
                     [(tr [:admin/subdivision]) (constantly (when subdivision
                                                              (tr [(keyword "subdivision" subdivision)])))]
                     [(tr [:admin/currency]) :group/currency]
                     [(tr [:admin/timezone]) :group/tz]]}]])]
     [:section.bx--col
      [:div.px-2.w-1-2
       [:p (tr [:admin/select-target-superdomain-group])]
       [ui-combo/combo-box {:id          "select-target-superdomain-group"
                            :items       superdomain-groups
                            :disabled    disabled?
                            :value       (or target-superdomain-group "")
                            :item->str   :group/name
                            :on-change   #(>evt [::set-target-superdomain-group %])
                            :placeholder (tr [:select/select])}]]
      (when target-superdomain-group
        [:div.mt-4
         [sortable-table/table
          {:items   [target-superdomain-group]
           :id-func :db/id
           :cols    [[(tr [:admin/group-name]) :group/name]
                     [(tr [:admin/timezone]) :group/tz]]}]])]
     [:section.bx--col
      (when (seq items)
        [sortable-table/table
         {:items              items
          :row-class-override "lg:items-start"
          :headerless?        true
          :id-func            :idx
          :cols               [[(tr [:admin/group-name]) #(render-input tr validation-ctx :group-name % disabled?)]
                               [(tr [:admin/group-domain-label]) #(render-input tr validation-ctx :group-domain % disabled?)]
                               [(tr [:admin/official-name-short])
                                #(vector :<>
                                         (render-input tr validation-ctx :short-name % disabled?)
                                         (render-municipality %))]
                               [(tr [:admin/official-name]) #(render-input tr validation-ctx :official-name % disabled?)]]
          :on-expand          (fn [idx] (>evt [::update-expanded-rows idx (not (contains? expanded-rows idx))]))
          :expanded-row-keys  expanded-rows
          :expanded-func      (fn [row]
                                [:<>
                                 [:div.flex.flex-1.flex-row.gap-4
                                  (if (all-selected? row)
                                    (de-select-all tr row disabled?)
                                    (select-all tr row disabled?))
                                  (->> available-permit-types
                                       (map #(render-checkbox tr row % disabled?))
                                       doall)]
                                 [:div.mt-6.w-full.justify-end.flex
                                  [ui/button {:kind     :primary
                                              :icon     i/remove
                                              :disabled disabled?
                                              :on-click #(>evt [::remove-item (:idx row)])}
                                   (tr [:admin/remove])]]])}])
      [ui/button {:kind     :primary
                  :icon     i/plus
                  :disabled disabled?
                  :on-click #(let [idx (or (some-> items last :idx inc) 1)]
                               (>evt [::add-new-item
                                      (->> available-permit-types
                                           (map (fn [p] [p true]))
                                           (into {:idx idx}))])
                               (>evt [::update-expanded-rows idx true]))}

       (tr [:admin/add-group])]]
     [batch-progress-section tr]
     [:div.mt-6.w-full.justify-end.flex.gap-2
      (if batch-job-running?
        [ui/button {:kind     :primary
                    :icon     i/circle-minus
                    :on-click #(>evt [::open-modal batch-id])}
         (tr [:cancel])]
        [ui/button {:kind     :primary
                    :icon     i/circle-minus
                    :on-click #(>evt [::stop-batch-group-creation batch-id])}
         (tr [:clear])])
      [ui/button {:kind     :primary
                  :disabled (or save-disabled? disabled?)
                  :icon     i/plus
                  :on-click #(>evt [::start-batch-group-creation])}
       (tr [:admin/create-groups])]]]))

(def route
  {:name        :view/admin.batch-group-creation
   :view        view
   :controllers [{:start (fn [_]
                           (>evt [::fetch-municipalities])
                           (>evt [::admin-superdomain/fetch-superdomains]))}]})
